import { Component, DestroyRef, inject, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { OtpComponent } from '../otp/otp.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModalResult } from '../../interfaces/modal-result.interface';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { DEFAULT_ERROR_TEXT, OTP_LENGTH } from '../../constants';
import { OtpRequestClientDto } from '../../dtos/otp-request.dto';
import { IconComponent } from '../icon/icon.component';
import { Observable } from 'rxjs';
import { OtpConfirmDto } from '../../dtos/otp-confirm.dto';
import { finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface OtpModalData<T = unknown> {
  otpRequest: OtpRequestClientDto;
  onSubmit: (otpConfirmDto: OtpConfirmDto) => Observable<T>;
  onResendOtp: () => Observable<OtpRequestClientDto>;
  successMessageKey?: string;
}

@Component({
  selector: 'tc-otp-modal',
  templateUrl: './otp-modal.component.html',
  styleUrls: ['./otp-modal.component.scss'],
  imports: [
    TranslatePipe,
    OtpComponent,
    IconComponent,
  ],
})
export class OtpModalComponent {
  dialogRef = inject(MatDialogRef<OtpModalComponent, ModalResult>);
  dialogData: OtpModalData = inject(MAT_DIALOG_DATA);
  private destroyRef = inject(DestroyRef);

  readonly otpDigitsFormArray: FormArray<FormControl<string>> = new FormArray(
    new Array(OTP_LENGTH).fill(null).map(() => new FormControl('', Validators.required)),
  );

  isLoading = signal<boolean>(false);
  error = signal<string>(null);
  state = signal<'otp' | 'success'>('otp');

  onSubmit(): void {
    this.error.set(null);

    switch (this.state()) {
      case 'otp':
        this.isLoading.set(true);
        const otpConfirmDto: OtpConfirmDto = {
          requestId: this.dialogData.otpRequest.requestId,
          otpCode: this.otpDigitsFormArray.getRawValue().join(''),
        };

        this.dialogData.onSubmit(otpConfirmDto)
          .pipe(
            finalize(() => this.isLoading.set(false)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({
            next: () => this.state.set('success'),
            error: error => this.error.set(error.error?.message || DEFAULT_ERROR_TEXT),
          });
        break;

      case 'success':
        this.dialogRef.close({ type: 'CONFIRM' })
        break;
    }
  }

  onResendOtp(): void {
    this.error.set(null);

    this.isLoading.set(true);
    this.dialogData.onResendOtp()
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: otpRequest => this.dialogData.otpRequest = otpRequest,
        error: error => this.error.set(error.error?.message || DEFAULT_ERROR_TEXT),
      });
  }
}
